// Minimal, safe markdown parser for blog posts -> plain AST (rendered as React elsewhere).
// Supports: ## headings, paragraphs, - / 1. lists (one nesting level), - [ ] checklists,
// ``` code blocks, | tables |, > callouts, --- rules. Inline: **bold**, `code`.

const LIST_RE = /^(\s*)([-*]|\d+\.)\s+(.*)$/;

function splitRow(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

export function parseMarkdown(md) {
  const lines = (md || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let para = [];
  const flushPara = () => {
    if (para.length) blocks.push({ type: "p", text: para.join(" ") });
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();

    if (t.startsWith("```")) {
      flushPara();
      const code = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) code.push(lines[i++]);
      i++;
      blocks.push({ type: "code", text: code.join("\n") });
      continue;
    }
    if (!t) { flushPara(); i++; continue; }
    if (/^-{3,}$/.test(t)) { flushPara(); i++; continue; }

    const h = t.match(/^(#{1,4})\s+(.+)$/);
    if (h) {
      flushPara();
      blocks.push({ type: "h", level: Math.max(2, Math.min(h[1].length, 4)), text: h[2].replace(/\*\*/g, "") });
      i++;
      continue;
    }

    if (t.startsWith(">")) {
      flushPara();
      const q = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) q.push(lines[i++].trim().replace(/^>\s?/, ""));
      const text = q.join(" ").trim();
      if (text) blocks.push({ type: "quote", text });
      continue;
    }

    if (t.startsWith("|")) {
      flushPara();
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++]);
      const cells = rows.filter((r) => !/^\s*\|?\s*:?-{2,}/.test(r)).map(splitRow);
      if (cells.length) blocks.push({ type: "table", head: cells[0], rows: cells.slice(1) });
      continue;
    }

    const m = line.match(LIST_RE);
    if (m && m[1].length < 2) {
      flushPara();
      const ordered = /\d/.test(m[2]);
      const items = [];
      while (i < lines.length) {
        const l = lines[i];
        if (!l.trim()) {
          // allow blank lines inside a list if the list continues
          const next = lines[i + 1];
          const nm = next && next.match(LIST_RE);
          if (nm && nm[1].length < 2 && /\d/.test(nm[2]) === ordered) { i++; continue; }
          if (nm && nm[1].length >= 2 && items.length) { i++; continue; }
          break;
        }
        const lm = l.match(LIST_RE);
        if (lm && lm[1].length < 2) {
          if (/\d/.test(lm[2]) !== ordered) break;
          let text = lm[3];
          const check = text.match(/^\[( |x|X)\]\s+(.*)$/);
          items.push({ text: check ? check[2] : text, check: !!check, children: [] });
          i++;
        } else if (lm && items.length) {
          items[items.length - 1].children.push(lm[3].replace(/^\[( |x|X)\]\s+/, ""));
          i++;
        } else if (/^\s{2,}\S/.test(l) && items.length) {
          const last = items[items.length - 1];
          if (last.children.length) last.children[last.children.length - 1] += " " + l.trim();
          else last.text += " " + l.trim();
          i++;
        } else break;
      }
      blocks.push({ type: ordered ? "ol" : "ul", items, checklist: items.length > 0 && items.every((x) => x.check) });
      continue;
    }

    para.push(t);
    i++;
  }
  flushPara();
  return blocks;
}

export function splitInline(text) {
  // returns [{t:"text"|"b"|"code", v}]
  const out = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g;
  let last = 0, m;
  while ((m = re.exec(text || ""))) {
    if (m.index > last) out.push({ t: "text", v: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith("**")) out.push({ t: "b", v: tok.slice(2, -2) });
    else if (tok.startsWith("`")) out.push({ t: "code", v: tok.slice(1, -1) });
    else {
      const lm = tok.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      out.push({ t: "a", v: lm[1], href: lm[2] });
    }
    last = m.index + m[0].length;
  }
  if (last < (text || "").length) out.push({ t: "text", v: text.slice(last) });
  return out;
}

export function headingId(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

export function tocFrom(md) {
  return parseMarkdown(md)
    .filter((b) => b.type === "h" && b.level === 2)
    .map((b) => ({ id: headingId(b.text), text: b.text }));
}

// Pick the n posts that share the most topic words with `current` (keyword, falling back to title).
// Generic words every post shares are ignored, so ties don't collapse to "the newest n posts".
const RELATED_STOP = new Set(
  "webinar webinars event events with that your from into what when best how practices guide tips proven strategies drive drives attendance b2b"
    .split(" ")
);
const topicWords = (p) =>
  new Set(String((p && (p.keyword || p.title)) || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !RELATED_STOP.has(w)));

export function relatedPosts(current, all, n = 3) {
  const mine = topicWords(current);
  return (all || [])
    .filter((p) => p && p.slug && p.slug !== current.slug)
    .map((p, i) => {
      let s = 0;
      topicWords(p).forEach((w) => { if (mine.has(w)) s++; });
      return { p, s, i };
    })
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, n)
    .map((x) => x.p);
}
